import { useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadLibraryCover } from "@/lib/libraryCovers";
import { resizeImageForCover } from "@/lib/imageResize";

const COVER_ACCEPT = "image/png,image/jpeg,image/webp";
const COVER_MAX_PRE_RESIZE_BYTES = 8 * 1024 * 1024;

export const CoverUploadButton = ({
  libraryItemId,
  onUploaded,
}: {
  libraryItemId: string | null | undefined;
  onUploaded: (ref: string) => void;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    if (!libraryItemId) {
      setError("Save this item first, then you can upload a cover.");
      return;
    }
    if (file.size > COVER_MAX_PRE_RESIZE_BYTES) {
      setError("Image is too large (max 8 MB).");
      return;
    }
    setBusy(true);
    try {
      const resized = await resizeImageForCover(file, { maxEdge: 600, quality: 0.82 });
      const { ref, error: uploadError } = await uploadLibraryCover(libraryItemId, resized);
      if (uploadError || !ref) {
        setError(uploadError ?? "Upload failed");
        return;
      }
      onUploaded(ref);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-1">
      <input
        ref={inputRef}
        type="file"
        accept={COVER_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={busy || !libraryItemId}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
        {busy ? "Uploading…" : "Upload image"}
      </Button>
      <p className="text-[10px] text-muted-foreground">
        PNG, JPEG, or WebP. Resized to 600 px on the long edge to keep storage small.
      </p>
      {error ? <p className="text-[11px] text-destructive">{error}</p> : null}
    </div>
  );
};
